import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { ChevronDownIcon } from '@radix-ui/react-icons';
import styled from 'styled-components';
import { Avatar, Dialog, ElevatedButton } from '@producer/ui-kit';

import { Close } from '../../../components/icons/Close';
import { MemberSelect } from '../components/member-select';
import { GroupAdd } from '../../../components/icons';
import { Note } from '../../../components/note/note';
import { breakPoint } from '../../../app/theme';
import { Tooltip } from '../../../components/tooltip/Tooltip';
import { RoleDropdown } from '../components/role-dropdown';
import { toastPool } from '../../toasts/models/toast-pool';
import { Toast } from '../../toasts/models/toast';
import { catchError } from '../../../core/catch-error';

import type { RoleTypes } from '../components/role-dropdown';
import type { Member, Project } from '../../../app/entities';
import { createMember } from '../../members/store/member.slice';
import { context } from '../../../app/context';

interface ProjectCollaboratorModalProps {
  project: Project;
  onCancel: () => void;
  isOpenProps?: boolean;
  onClose: () => void;
}

export const ProjectCollaboratorModal: React.FC<ProjectCollaboratorModalProps> = observer(
  ({ project, onCancel, onClose }) => {
    const currentMember = context.member;

    const [actionDisabled, setActionDisabled] = useState<boolean>(false);
    const [hint, setHint] = useState<string>();
    const [serverError, setServerError] = useState<string>();

    const dropdownWidth = React.useRef<number>(0);

    const handleRoleChange = (role?: RoleTypes) => {
      if (role === 'creator') {
        const limit = project.space.seats?.limit || 1;
        const current = project.space.seats?.current || 1;

        if (current < limit) {
          return;
        }

        if (project.space.subscription?.subscriptionId && !project.space.isSelfServing) {
          setHint('All seats taken. Reach out to customer support to extend your plan.');
          setActionDisabled(true);
        } else if (project.space.isSelfServing) {
          setHint("You'll be charged on adding a billable creator.");
        }
      } else {
        setHint(undefined);
        setActionDisabled(false);
      }
    };

    const promoteCollaborator = async (collaborator: Member, role: RoleTypes = 'guest') => {
      if (collaborator.role === role || role === 'guest') {
        return;
      }

      if (role === 'creator') {
        try {
          await collaborator.promote();
          toastPool.insert(
            new Toast(
              `${collaborator.fullName} successfully added as an additional creator. Billing information sent via email.`,
              'success',
            ),
          );
        } catch (error) {
          catchError(error);
        }
      }
    };

    const listContainerCallbackRef = (node: HTMLDivElement) => {
      if (!node) {
        return;
      }
      dropdownWidth.current = node.clientWidth - 72;
    };

    const handleSubmit = async (email: string, role?: RoleTypes) => {
      try {
        let member = project.space.members.find((el) => el.email === email);

        if (!member && role) {
          const members = await createMember({
            spaceId: project.spaceId,
            members: [
              {
                email,
                role,
              },
            ],
          });

          member = members[0];
        }

        if (!member || !member._id) {
          return;
        }

        await project.addCollaborator(member._id);

        onClose();

        toastPool.insert(new Toast(`Successfully invited ${email} to the project`, 'success'));
        setServerError(undefined);
      } catch (error) {
        setServerError('Something went wrong. Please try again');
      }
    };

    return (
      <Dialog title="Manage people in project" onClose={onCancel} mobilePosition="bottom" hideClose>
        <Content>
          {hint && <Note isFullWidth>{hint}</Note>}

          {serverError ? (
            <ServerError>
              <Note variant="danger" isFullWidth>
                {serverError}
              </Note>

              {setServerError && (
                <ElevatedButton text="Try again" onClick={() => setServerError(undefined)} />
              )}
            </ServerError>
          ) : (
            <MemberSelect
              space={project.space}
              collaborators={project.collaborators}
              actionDisabled={actionDisabled}
              onSubmit={handleSubmit}
              onRoleChange={handleRoleChange}
            />
          )}
        </Content>

        {project.collaborators?.length ? (
          <List ref={listContainerCallbackRef}>
            {project.collaborators.map((collaborator) => {
              const { email, _id, fullName } = collaborator;

              return (
                <CollaboratorItem key={_id}>
                  <Left>
                    <Avatar src={collaborator.avatar} initials={collaborator.initials} />

                    <MemberInfo>
                      <span>{fullName}</span>
                      <span>{email}</span>
                    </MemberInfo>
                  </Left>

                  <Right>
                    {collaborator.role === 'guest' ? (
                      <RoleDropdown
                        trigger={(label) => (
                          <Trigger>
                            <Tooltip text="Promote to creator">
                              <TooltipChild>
                                <RoleLabel>{label}</RoleLabel>

                                <ChevronDownIcon color="var(--color-grayscale-shuttle)" />
                              </TooltipChild>
                            </Tooltip>
                          </Trigger>
                        )}
                        space={project.space}
                        role={collaborator.role}
                        width={dropdownWidth.current}
                        onChange={(role) => promoteCollaborator(collaborator, role)}
                      />
                    ) : (
                      <RoleLabel>{collaborator.role || 'guest'}</RoleLabel>
                    )}

                    {['owner', 'admin'].includes(currentMember.role) && (
                      <Tooltip text="Remove from project">
                        <CloseButton onClick={() => project.removeCollaborator(collaborator._id)}>
                          <Close fill="var(--color-grayscale-light-slate)" />
                        </CloseButton>
                      </Tooltip>
                    )}
                  </Right>
                </CollaboratorItem>
              );
            })}
          </List>
        ) : (
          <CollaboratorsPlaceholder>
            <GroupAdd />

            <span>No people in this project yet.</span>
          </CollaboratorsPlaceholder>
        )}
      </Dialog>
    );
  },
);

const Right = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.8rem;
  align-items: center;
`;

const CloseButton = styled.button`
  all: unset;
  cursor: pointer;
  border-radius: 0.4rem;
  display: flex;
  height: 2.4rem;
  width: 2.4rem;
  justify-content: center;
  align-items: center;

  svg {
    width: 0.8rem;
    height: 0.8rem;
  }

  &:hover {
    cursor: pointer;
    background-color: var(--color-grayscale-arsenic);
    & svg,
    & svg > * {
      fill: var(--color-grayscale-white);
    }
  }
`;

const CollaboratorItem = styled.div`
  display: flex;
  gap: 0.8rem;
  align-items: center;
  justify-content: space-between;
`;

const Left = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.8rem;
`;

const MemberInfo = styled.div`
  display: flex;
  flex-direction: column;

  span:first-child {
    color: var(--color-grayscale-white, #fafafa);
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 1.4rem;
    font-weight: 400;
    line-height: 1.8rem;
  }

  span:last-child {
    color: var(---color-grayscale-light-slate, #7a8296);
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 1.2rem;
    font-weight: 400;
    line-height: 1.4rem;
  }
`;

const List = styled.div`
  border-top: 0.1rem solid var(--color-stroke-default);
  padding: 1.6rem 0;
  margin-top: 1.6rem;
  display: flex;
  flex-direction: column;
  gap: 1.6rem;
  width: 100%;
  overflow-y: scroll;
  overflow-x: hidden;

  @media screen and (min-width: ${breakPoint.medium}px) {
    height: 26.4rem;
  }
`;

const CollaboratorsPlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  justify-content: center;
  align-items: center;
  height: 26.4rem;

  margin-top: 1.6rem;
  padding: 2.4rem 1.6rem;
  border-top: 0.1rem solid var(--color-grayscale-tuna);

  & > span {
    color: var(--color-grayscale-shuttle, #626878);
    text-align: center;
    font-size: 1.6rem;
    font-weight: 400;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  cursor: default;
  gap: 1.6rem;

  @media screen and (min-width: ${breakPoint.medium}px) {
    max-width: min(48rem, 80vw);
    min-width: min(48rem, 80vw);
  }
`;

const ServerError = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2.4rem;
  align-items: center;
  justify-content: center;
  padding: 2.4rem 0;
`;

const RoleLabel = styled.div`
  color: var(--color-grayscale-white, #fafafa);
  text-transform: capitalize;
  font-size: 1.4rem;
  font-weight: 400;
  user-select: none;
`;

const Trigger = styled.button`
  all: unset;
  user-select: none;
  display: flex;
  cursor: pointer;

  & + *[data-radix-popper-content-wrapper] {
    & > [data-radix-menu-content] {
      backdrop-filter: unset;
      background: var(--color-grayscale-tuna, #31343c);
      box-shadow: 0px 10px 15px -3px rgba(0, 0, 0, 0.1);
      border-radius: var(--border-radius-small);
    }
  }
`;

const TooltipChild = styled.div`
  user-select: none;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.4rem;
`;
